/**
 * Web component: Newsletter signup (newsletter-signup)
 * -----------------------------------------------------------------------------
 * Newsletter section used in the footer.
 *
 */
import classes from '~/config/classes';
import { buildQueryString } from '~/core/query';
import './newsletter-signup.scss';
import intlTelInput from 'intl-tel-input';
import { de, en, es, fr, it, nl, pl } from 'intl-tel-input/i18n';

import { vueReady } from '~/core/runners';

const selectors = {
  form: '[js-newsletter-signup="form"]',
  input: '[js-newsletter-signup="input"]',
  error: '[js-newsletter-signup="error"]',
  phone_input: '[js-newsletter-signup="phone_input"]',
  phone_error: '[js-newsletter-signup="phone_error"]',
};

/**
 * Create new <newsletter-signup> custom element.
 */
class NewsletterSignup extends HTMLElement {
  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super();
    this.$data = {};
    this.flagUrl = 'https://cdn.shopify.com/static/images/flags/';
    this.iti = '';
  }

  data(component) {
    return {
      selectors: {
        form: component.querySelector(selectors.form),
        input: component.querySelector(selectors.input),
        errorMessage: component.querySelector(selectors.error),
        phoneInput: component.querySelector(selectors.phone_input),
        phoneErrorMessage: component.querySelector(selectors.phone_error),
      },
    };
  }

  /**
   * Lifecycle hook - component is attached to DOM.
   */
  connectedCallback() {
    this.$data = this.data(this);

    if (this.$data.selectors.phoneInput) {
      const selectableCountries =
        cnvs.settings.newsletter.country_codes.replace(/\s/g, '');

      this.iti = intlTelInput(this.$data.selectors.phoneInput, {
        i18n: this.getLanguage(),
        onlyCountries: selectableCountries.split(','),
        initialCountry: cnvs.store.country,
        separateDialCode: true,
        autoPlaceholder: 'aggressive',
        loadUtils: () => import('intl-tel-input/utils'),
      });
    }

    this.$data.selectors.form.addEventListener('submit', (event) => {
      this.handleFormSubmit(event);
    });
  }

  getLanguage() {
    switch (cnvs.store.country) {
      case 'DE':
        return de;
      case 'ES':
        return es;
      case 'FR':
        return fr;
      case 'IT':
        return it;
      case 'NL':
        return nl;
      case 'PL':
        return pl;
      default:
        return en;
    }
  }

  /**
   * Handle form submit event and message state based on response.
   * @param {SubmitEvent} event - Form submit event
   */
  async handleFormSubmit(event) {
    event.preventDefault();

    const { errorMessage, input, phoneInput, phoneErrorMessage } =
      this.$data.selectors;
    let phoneNumber = '';

    const formData = {
      email: input.value,
      formId: this.dataset.formId,
      listId: this.dataset.listId,
    };

    if (phoneInput && phoneInput.value.length > 0) {
      const countryData = this.iti.getSelectedCountryData();
      const removeSpaces = (str) => str.replace(/\s/g, '');
      phoneNumber = `+${countryData.dialCode}${parseInt(removeSpaces(phoneInput.value))}`;

      this.handleInputReset(phoneInput, phoneErrorMessage);
      if (!this.isValidNumber(phoneNumber)) {
        phoneErrorMessage.innerText = cnvs.strings.newsletter.invalid;
        this.handleError(phoneInput, phoneErrorMessage);
        return;
      }

      formData.phone = phoneNumber;
      formData.phoneFormId = this.dataset.phoneFormId;
      formData.phoneListId = this.dataset.phoneListId;

      document
        .querySelector('.newsletter-signup-overlay__phone')
        .classList.remove('hidden');
    }

    // Check if listName exists in dataset and add it to formData
    if (this.dataset.listName) {
      formData.listName = this.dataset.listName;
    }

    this.handleInputReset(input, errorMessage);

    this.subscribeTracking();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email)) {
      this.handleError(input, errorMessage);
      return;
    }

    // Mock success and error states
    if (formData.email === 'success@esn.de') {
      this.handleInputSuccess(input, errorMessage);
      this.handleSuccess();
      return;
    }

    if (formData.email === 'error@esn.de') {
      this.handleError(input, errorMessage);
      return;
    }
    // End mock

    // Build the query string from form data
    const queryString = buildQueryString(formData);
    // Construct the API URL
    const baseUrl = `${window.location.protocol}//${window.location.host}/apps/custom-klaviyo/`;
    const apiUrl = `${baseUrl}?${queryString}`;

    // Fetch data from the Klaviyo API
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        response.json().then((errorData) => {
          // Handle the error and access the error message
          this.handleKlaviyoErrors(errorData.error);
          throw new Error(`Request failed with status ${response.status}`);
        });
      }

      const data = await response.json();

      const listId = data.listId;

      // Extract data array from profileLists
      const profileListsData = data.profileLists?.data;

      if (profileListsData) {
        // Check if listId exists in the array of profileLists by id
        const listExists = profileListsData.some((list) => list.id === listId);

        if (listExists) {
          this.handleInputSuccess(input, errorMessage);
          this.handleAlreadySubscribed();
          this.closeNewsletterPopup();

          if (phoneInput) {
            this.handleInputSuccess(phoneInput, phoneErrorMessage);
          }

          return;
        }
      }

      this.handleSuccess();
      this.handleInputSuccess(input, errorMessage);

      if (phoneInput) {
        this.handleInputSuccess(phoneInput, phoneErrorMessage);
      }
    } catch (error) {
      throw new Error(`Request failed with status ${error}`);
    }
  }

  handleKlaviyoErrors(error) {
    const { input, phoneInput, phoneErrorMessage, errorMessage } =
      this.$data.selectors;
    if (error === 'invalid') {
      phoneErrorMessage.innerText = cnvs.strings.newsletter.invalid;
      this.handleError(phoneInput, phoneErrorMessage);
    }

    if (error === 'duplicate_profile') {
      this.handleInputReset(input, errorMessage);
      this.handleInputReset(phoneInput, phoneErrorMessage);
      this.handleAlreadySubscribed();

      // as it returns in error it would open the popup again, so we close it here
      this.closeNewsletterPopup();
    }
  }

  handleInputSuccess(input, error) {
    input.classList.remove(classes.error);
    input.classList.add(classes.success);
    error.classList.add(classes.hidden);
    error.setAttribute('aria-hidden', true);
  }

  /**
   * Handle success state.
   */
  handleAlreadySubscribed() {
    cnvs.EventBus.emit('overlay:open', {
      ignoreDismissed: true,
      namespace: 'newsletterSignupAlreadySubscribed',
    });
  }

  isValidNumber(phoneNumber) {
    const { phoneErrorMessage, phoneInput } = this.$data.selectors;
    const checkE164 = /^\+[1-9]\d{10,14}$/;

    if (
      phoneInput &&
      phoneInput.value.length > 0 &&
      !this.iti.isValidNumber() &&
      checkE164.test(phoneNumber) === false
    ) {
      this.handleError(phoneInput, phoneErrorMessage);
      return false;
    }

    return true;
  }

  /**
   * Handle success state.
   */
  closeNewsletterPopup() {
    cnvs.EventBus.emit('overlay:close', {
      namespace: 'newsletterPopup',
    });
  }

  handleSuccess() {
    this.closeNewsletterPopup();

    cnvs.EventBus.emit('overlay:open', {
      ignoreDismissed: true,
      namespace: `newsletterSignupSuccess--${this.dataset.formId}`,
    });
  }

  handleInputReset(input, error) {
    input.classList.remove(classes.success);
    input.classList.remove(classes.error);
    error.classList.add(classes.hidden);
    error.setAttribute('aria-hidden', true);
  }

  /**
   * Handle error state.
   */
  handleError(input, error) {
    input.classList.add(classes.error);
    input.classList.remove(classes.success);
    error.classList.remove(classes.hidden);
    error.setAttribute('aria-hidden', false);

    // Tracking error event
    this.subscribeErrorTracking(error.innerText);
  }

  /**
   * Subscribe tracking event handler
   * - Getting location from closest newsletter-signup
   * element with dataset namespace
   * dataLayer:newsletter_subscribe_soi
   */
  subscribeTracking() {
    cnvs.EventBus.emit('dataLayer:newsletter_subscribe_soi', {
      event: 'newsletter_subscribe_soi',
      location: this.closest('newsletter-signup').dataset.namespace,
    });
  }

  /**
   * Subscribe error event Tracking
   * @param {String} errorMessage - Error message
   * dataLayer:newsletter_subscribe_error
   */
  subscribeErrorTracking(errorMessage) {
    cnvs.EventBus.emit('dataLayer:newsletter_subscribe_error', {
      event: 'newsletter_subscribe_error',
      errorMessage,
      location: this.closest('newsletter-signup').dataset.namespace,
    });
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('newsletter-signup')) {
    return;
  }

  customElements.define('newsletter-signup', NewsletterSignup);
});
